import { textFormatting, hdrm, view, globals } from '@yola/ws-sdk';
import highlighter from 'src/js/modules/highlighter';
import designAutomation from 'src/js/modules/design-automation';
import { REGULAR_IMAGE_ATTRIBUTES_FOR_SHALLOW_PROXY } from 'src/js/modules/blocks/constants/common';
import getAttributesOperations from './get-attributes-operations';
import layoutSwitchingHelpers from './layout-switching';
import blockSettingsHelpers from './block-settings';
import trackBlockLayoutSwitched from '../trackers/track-block-layout-switched';
import optionTypes from '../constants/display-options-type';
import trackBlockSettingsDialogSubmitted from '../trackers/track-block-settings-dialog-submitted';
import dropTextResizing from './layout-switching/drop-text-resizing';
import getBackgroundAttributeOperations from './get-background-attribute-operations';
import removeCoverConstraintsAttribute from './remove-cover-constraints-attribute';
import convertAttributeOperationToAction from './convert-attribute-operation-to-action';
import { CONTAINER_WIDTH_CLASS } from '../constants/container-width';

const { replaceOptionalChildren, getChildrenToUpdate, prepareReplacedData } =
  layoutSwitchingHelpers;

const {
  getChanges,
  hasTouchedGlobals,
  handleOptions,
  handleBackgroundOption,
  createBreakpointsSliderOption,
  getContainerWidthClassValue,
} = blockSettingsHelpers;

const { updateStrategies } = hdrm.constants;
const { webkitWorkarounds } = hdrm.helpers;

const saveBlockSettingsChanges = ({
  intermediateBlockId,
  destinationBlockNode,
  onDialogMainAction,
  isClickOnOverlay,
  optionalChildren,
  displayOptions: stateDisplayOptions,
  backgroundOption,
  cachedBackgroundOption,
  hideDialog,
  bulkViewActions,
  originalBlock,
  blockNode,
  blockId: newBlockId,
  variationId: newBlockVariationId,
  refId,
  setCleanUrlsEnabled,
  initialElement,
  usedCustomColorOptions,
  isOnlineStoreBlock,
  forceReloadView,
}) => {
  const safeDisplayOptions = stateDisplayOptions || [];

  const displayOptions = safeDisplayOptions.map((option) =>
    option.id === 'whitespace_top' || option.id === 'whitespace_bottom'
      ? createBreakpointsSliderOption({
          item: option,
          displayOptions: safeDisplayOptions,
          blockNode,
        })
      : option
  );

  const isBlockLayoutAdjusted = Boolean(destinationBlockNode);

  const blockSettingsChanges = getChanges({
    optionalChildren,
    displayOptions,
  });

  trackBlockSettingsDialogSubmitted({
    isOverlayClicked: isClickOnOverlay,
    onDialogMainAction,
    blockId: intermediateBlockId,
    blockVariationId: newBlockVariationId,
    blockLayoutAdjusted: isBlockLayoutAdjusted,
    blockSettingsChanges,
    backgroundOption,
  });

  if (isBlockLayoutAdjusted) {
    highlighter.operations.hide([blockNode]);
    const { blockId: oldBlockId } = originalBlock;

    optionalChildren.forEach((item) => {
      hdrm.helpers.updateChildrenPresence(
        destinationBlockNode,
        item.querySelector,
        item.isEnabled,
        item.id
      );
    });

    const optionalChildrenToUpdate = getChildrenToUpdate(
      originalBlock.blockNode,
      destinationBlockNode
    );

    const attributesForShallowProxy =
      newBlockVariationId !== originalBlock.blockVariationId
        ? REGULAR_IMAGE_ATTRIBUTES_FOR_SHALLOW_PROXY
        : [];

    const replacedData = replaceOptionalChildren({
      optionalChildrenToUpdate,
      originalBlockNode: originalBlock.staticBlockNode,
      destinationBlockNode,
      attributesForShallowProxy,
    });
    const { targetText, replacedText } = prepareReplacedData(replacedData);

    if (newBlockVariationId !== originalBlock.blockVariationId) {
      dropTextResizing(destinationBlockNode);
    }

    handleOptions(destinationBlockNode, displayOptions);

    handleBackgroundOption({
      destinationBlockNode,
      backgroundOption: backgroundOption || cachedBackgroundOption,
    });

    const replaceOptions = { strategy: updateStrategies.UPDATE_BOTH };

    const staticDocument = originalBlock.staticBlockNode.ownerDocument;
    const nodeToReplace = staticDocument.importNode(destinationBlockNode, true);
    webkitWorkarounds.removeLazyLoadingAttributeFromImages(nodeToReplace);

    if (usedCustomColorOptions) {
      const { globalId, html } = textFormatting.helpers.updateCustomColorStylesheetInGlobal(false);
      const actions = [
        globals.actions.setGlobalHTMLAndAffectHistory(globalId, html),
        view.actions.replaceElement(nodeToReplace, refId, replaceOptions),
      ];
      bulkViewActions(actions);
    } else {
      view.operations.replaceElement(nodeToReplace, refId, replaceOptions);
    }

    trackBlockLayoutSwitched({
      oldBlockId,
      newBlockId,
      oldBlockVariationId: originalBlock.blockVariationId,
      newBlockVariationId,
      oldText: replacedText,
      newText: targetText,
    });

    hideDialog();
    return;
  }

  const actions = [];
  const [changedChildren, changesList] = blockSettingsChanges;

  const attributesOperations = getAttributesOperations(initialElement, changesList, displayOptions);
  const isSurfaceChanged = changesList.some((item) => item.type === optionTypes.SURFACE);
  const backgroundAttributesOperations = getBackgroundAttributeOperations({
    backgroundOption,
    displayOptions,
  });

  [...attributesOperations, ...backgroundAttributesOperations].forEach((operation) => {
    const action = convertAttributeOperationToAction(operation);
    actions.push(action);

    // Here we detect attributes names which should be marked if modified by user.
    // This is needed to distinguish them during UI automation processes.
    const { elementId, name } = operation;
    const changedOption = changesList.find(({ attribute }) => attribute === name);

    if (changedOption?.userModified) {
      actions.push(designAutomation.actions.addUserModifiedAttribute(elementId, name));
    }
  });

  const removeCoverConstraintsAction = removeCoverConstraintsAttribute(originalBlock, blockNode);

  if (removeCoverConstraintsAction) {
    actions.push(removeCoverConstraintsAction);
  }

  const changedContainerWidthOption = changesList.find(
    (item) => item.type === optionTypes.CONTAINER_WIDTH
  );

  if (changedContainerWidthOption) {
    const prevClassValue = getContainerWidthClassValue(
      changedContainerWidthOption.initialValue,
      changedContainerWidthOption.initialCustomValue
    );

    const classValue = getContainerWidthClassValue(
      changedContainerWidthOption.value,
      changedContainerWidthOption.customValue
    );

    if (classValue) {
      actions.push(view.actions.setResponsiveClass(refId, CONTAINER_WIDTH_CLASS, classValue));
    } else if (prevClassValue) {
      actions.push(view.actions.removeResponsiveClass(refId, CONTAINER_WIDTH_CLASS));
    }
  }

  if (changedChildren.length) {
    const childrenPresenceUpdates = changedChildren.map((child) => ({
      blockSettingId: child.id,
      childrenSelector: child.querySelector,
      enabled: child.isEnabled,
    }));
    const childrenPresenceOptions = { strategy: updateStrategies.UPDATE_STATIC_ONLY };

    actions.push(
      view.actions.bulkSetChildrenPresence(refId, childrenPresenceUpdates, childrenPresenceOptions)
    );
  }

  if (usedCustomColorOptions && isSurfaceChanged) {
    const { globalId, html } = textFormatting.helpers.updateCustomColorStylesheetInGlobal(false);
    actions.push(globals.actions.setGlobalHTMLAndAffectHistory(globalId, html));
  }

  if (actions.length) {
    const isStaticOnlyStrategy =
      (!hasTouchedGlobals(changedChildren) && hasTouchedGlobals(optionalChildren)) ||
      isOnlineStoreBlock;

    bulkViewActions(actions, {
      ...(isStaticOnlyStrategy && {
        strategy: updateStrategies.UPDATE_STATIC_ONLY,
      }),
    });
  } else if (hasTouchedGlobals(optionalChildren)) {
    forceReloadView();
  }

  const changedOnlineStoreCleanUrlSelector = changesList.find(
    (item) => item.type === optionTypes.ONLINE_STORE_CLEAN_URL_SELECTOR
  );

  if (changedOnlineStoreCleanUrlSelector) {
    setCleanUrlsEnabled(changedOnlineStoreCleanUrlSelector.isEnabled);
  }

  hideDialog();
};

export default saveBlockSettingsChanges;
